import { atomWithHash } from '@heidi/utils/jotai-helpers';
import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

/**
 * Tracks whether the user has visited the new-user route during this session.
 * This is used to determine if modals should show after user creation.
 */
export const hasVisitedNewUserRouteAtom = atom<boolean>(false);

/**
 * Tracks whether the user has completed onboarding in this session.
 * This prevents the onboarding modal from showing after completion.
 */
export const hasCompletedOnboardingAtom = atom<boolean>(false);

export type MobileSignupSource = 'ios' | 'android' | 'desktop';

const VALID_MOBILE_SIGNUP_SOURCES: ReadonlySet<string> = new Set<MobileSignupSource>([
  'ios',
  'android',
  'desktop',
]);

export function isValidMobileSignupSource(value: unknown): value is MobileSignupSource {
  return typeof value === 'string' && VALID_MOBILE_SIGNUP_SOURCES.has(value);
}

const mobileSourceSessionStorage = createJSONStorage<MobileSignupSource | null>(
  () => sessionStorage
);

/**
 * Stores the mobile signup source (ios/android/desktop) from the URL `source` param.
 * Persisted in sessionStorage so it survives the Auth0/Kinde redirect cycle
 * but clears when the webview tab closes.
 */
export const mobileSignupSourceAtom = atomWithStorage<MobileSignupSource | null>(
  'mobile-signup-source',
  null,
  mobileSourceSessionStorage
);

/**
 * Stores the affiliate code from URL parameter during user onboarding
 * This is used to track referral sources and attribute new user signups
 */
export const affiliateCodeAtom = atomWithHash<string | undefined>(
  'affiliateCode',
  undefined,
  {
    setHash: 'replaceState',
  }
);

/**
 * Stores the referral code from URL parameter during user onboarding
 * This is a generic referral code that works with any campaign
 */
export const refCodeAtom = atomWithHash<string | undefined>('refCode', undefined, {
  setHash: 'replaceState',
});

/**
 * Stores the referral campaign from URL parameter during user onboarding
 * This tracks which campaign the referral link belongs to (e.g., 'evidence')
 */
export const referralCampaignAtom = atomWithHash<string | undefined>(
  'referralCampaign',
  undefined,
  {
    setHash: 'replaceState',
  }
);
